/**
 * Générateur — Automatisme : reconnaître les solides
 * Attendu ID : 1019 | Niveau : 6ème | Domaine : GM (vision dans l'espace)
 * Rubrique : AUTOMATISME (programme 2025) — « L'élève identifie dans un
 * ensemble de solides lesquels sont des pyramides, des boules, des cubes,
 * des cylindres, des pavés, des cônes ou des prismes droits. »
 * Labels (cartes exerciseur) :
 *   - Reconnaître les solides
 *
 * ── Plan de cartes (2026-08-22, validé prof) ──────────────────────────────
 * Sept solides, un seul verbe : IDENTIFIER (les items comptage de
 * faces/arêtes et l'inclusion cube/pavé de la fiche sont HORS automatisme).
 * Trois formats, pas d'objets du quotidien (arbitrage prof : application,
 * pas automatisme). Perspective cavalière avec ARÊTES CACHÉES EN
 * POINTILLÉS (explicitement au programme — la réserve de l'agent sur la
 * fiche source, corrigée ici) ; variantes par proportions et sens de
 * fuyante, jamais de rotation fantaisiste (vues canoniques).
 *   F1 nommer_qcm — un solide, « Quel est le nom LE PLUS PRÉCIS de ce
 *      solide ? », 4 noms. « Le plus précis » désamorce les inclusions
 *      vraies (un cube EST un pavé droit et un prisme droit — item 9 de la
 *      fiche) : les distracteurs-confusions restent servables sans être
 *      défendables.
 *   F2 trouver_qcm — planche 2×2, « Lequel de ces solides est un cône ? ».
 *   F3 trier_multi — planche 6, « Lesquels de ces solides sont des
 *      pyramides ? » (la formulation littérale de l'attendu), 1 à 3
 *      exemplaires de la cible en proportions différentes.
 * VERROUS D'INCLUSION (planches F2/F3) : cible « pavé droit » → jamais de
 * cube dans la planche ; cible « prisme droit » → jamais de cube NI de
 * pavé (ce sont des prismes droits particuliers) ; une seule classe cible.
 * SOLVEUR figure-based : chaque solide est RECLASSÉ depuis ses primitives
 * SVG mesurées (cercle → boule ; ellipse → cylindre ; arcs sans ellipse →
 * cône ; deux triangles partageant un sommet → pyramide ; un triangle + un
 * parallélogramme accolé → prisme ; trois quadrilatères dont un rectangle
 * droit → cube si carré, pavé sinon) — aucun attribut ne dit la nature, et
 * le pavé a par construction une face avant NON carrée (vues canoniques).
 */
import {
    randomChoice, randInt, randomChoiceMultiple, shuffle,
    makeQcm,
} from '../../../utils';

const ATTENDU_ID = 1019;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Reconnaître les solides';

const garde = (condition, message) => {
    if (!condition) throw new Error(`gen_1019 : ${message}`);
};

const dataUrl = (svg) => `data:image/svg+xml,${encodeURIComponent(svg)}`;
const elImage = (url) => ({ version: 1, elements: [{ type: 'image', config: { imageUrl: url } }] });

const BLEU = '#1E3A8A';
const FACE1 = '#eff6ff';
const FACE2 = '#dbeafe';
const NUMS = ['①', '②', '③', '④', '⑤', '⑥'];

// ─── Les sept solides : primitives abstraites + bbox ──────────────────────
// parts : {t:'poly',pts,fill} | {t:'line',a,b,dash} | {t:'ellipse',cx,cy,rx,ry}
//         {t:'circle',cx,cy,r} | {t:'arc',x1,y1,x2,y2,rx,ry,sweep,dash}

const SOLIDES = {
    cube: () => {
        const a = randInt(42, 56);
        const k = 0.42 + Math.random() * 0.1;
        const sx = randomChoice([1, -1]);
        return boiteParts(a, a, a * k * 1.15 * sx, -a * k);
    },
    pave: () => {
        const h = randInt(32, 42);
        const w = Math.round(h * (1.45 + Math.random() * 0.4));
        const p = randInt(24, 32);
        const sx = randomChoice([1, -1]);
        return boiteParts(w, h, p * sx, -p * 0.8);
    },
    pyramide: () => {
        const b = randInt(48, 62);
        const H = Math.round(b * (0.85 + Math.random() * 0.3));
        const sx = randomChoice([1, -1]);
        const dx = sx * Math.round(b * (0.35 + Math.random() * 0.12));
        const dy = -Math.round(b * 0.3);
        const fL = [0, 0];
        const fR = [b, 0];
        const bNear = sx > 0 ? [b + dx, dy] : [dx, dy];
        const bFar = sx > 0 ? [dx, dy] : [b + dx, dy];
        const cx = (fL[0] + fR[0] + b + 2 * dx) / 4;
        const S = [cx, dy / 2 - H];
        const parts = [
            { t: 'poly', pts: [S, fL, fR], fill: FACE1 },
            { t: 'poly', pts: sx > 0 ? [S, fR, bNear] : [S, fL, bNear], fill: FACE2 },
            { t: 'line', a: bFar, b: bNear, dash: true },
            { t: 'line', a: bFar, b: sx > 0 ? fL : fR, dash: true },
            { t: 'line', a: S, b: bFar, dash: true },
        ];
        return parts;
    },
    prisme: () => {
        const bt = randInt(40, 54);
        const ht = Math.round(bt * (0.7 + Math.random() * 0.3));
        const prof = randInt(30, 42);
        const sx = randomChoice([1, -1]);
        const o = [sx * prof * 0.85, -prof * 0.55];
        const T = [bt / 2, 0];
        const BL = [0, ht];
        const BR = [bt, ht];
        const add = (P) => [P[0] + o[0], P[1] + o[1]];
        const near = sx > 0 ? BR : BL;
        const far = sx > 0 ? BL : BR;
        return [
            { t: 'poly', pts: [T, near, add(near), add(T)], fill: FACE2 },
            { t: 'poly', pts: [T, BL, BR], fill: FACE1 },
            { t: 'line', a: far, b: add(far), dash: true },
            { t: 'line', a: add(far), b: add(T), dash: true },
            { t: 'line', a: add(far), b: add(near), dash: true },
        ];
    },
    cylindre: () => {
        const rx = randInt(20, 28);
        const H = Math.round(rx * (1.5 + Math.random() * 1.0));
        const ry = Math.max(6, Math.round(rx * 0.32));
        const cyB = ry + H;
        return [
            { t: 'line', a: [0, ry], b: [0, cyB], dash: false },
            { t: 'line', a: [2 * rx, ry], b: [2 * rx, cyB], dash: false },
            { t: 'arc', x1: 0, y1: cyB, x2: 2 * rx, y2: cyB, rx, ry, sweep: 0, dash: false },
            { t: 'arc', x1: 0, y1: cyB, x2: 2 * rx, y2: cyB, rx, ry, sweep: 1, dash: true },
            { t: 'ellipse', cx: rx, cy: ry, rx, ry },
        ];
    },
    cone: () => {
        const rx = randInt(24, 32);
        const H = Math.round(rx * (1.7 + Math.random() * 0.8));
        const ry = Math.max(7, Math.round(rx * 0.3));
        return [
            { t: 'line', a: [rx, 0], b: [0, H], dash: false },
            { t: 'line', a: [rx, 0], b: [2 * rx, H], dash: false },
            { t: 'arc', x1: 0, y1: H, x2: 2 * rx, y2: H, rx, ry, sweep: 0, dash: false },
            { t: 'arc', x1: 0, y1: H, x2: 2 * rx, y2: H, rx, ry, sweep: 1, dash: true },
        ];
    },
    boule: () => {
        const R = randInt(28, 36);
        const ry = Math.round(R * 0.34);
        return [
            { t: 'circle', cx: R, cy: R, r: R },
            { t: 'arc', x1: 0, y1: R, x2: 2 * R, y2: R, rx: R, ry, sweep: 0, dash: false },
            { t: 'arc', x1: 0, y1: R, x2: 2 * R, y2: R, rx: R, ry, sweep: 1, dash: true },
        ];
    },
};

/** Le pavé/cube générique : face avant w×h, fuyante (dx, dy vers le haut). */
const boiteParts = (w, h, dx, dy) => {
    const fTL = [0, 0];
    const fTR = [w, 0];
    const fBR = [w, h];
    const fBL = [0, h];
    const add = (P) => [P[0] + dx, P[1] + dy];
    const droite = dx > 0;
    const cache = droite ? add(fBL) : add(fBR);   // le sommet arrière invisible
    return [
        { t: 'poly', pts: [fTL, fTR, add(fTR), add(fTL)], fill: FACE2 },
        {
            t: 'poly',
            pts: droite ? [fTR, fBR, add(fBR), add(fTR)] : [fTL, fBL, add(fBL), add(fTL)],
            fill: FACE2,
        },
        { t: 'poly', pts: [fTL, fTR, fBR, fBL], fill: FACE1 },
        { t: 'line', a: cache, b: droite ? fBL : fBR, dash: true },
        { t: 'line', a: cache, b: droite ? add(fBR) : add(fBL), dash: true },
        { t: 'line', a: cache, b: droite ? add(fTL) : add(fTR), dash: true },
    ];
};

/** Bbox des primitives (les arcs comptent leur flèche d'ellipse). */
const bboxDe = (parts) => {
    const xs = [];
    const ys = [];
    for (const p of parts) {
        if (p.t === 'poly') p.pts.forEach(([x, y]) => { xs.push(x); ys.push(y); });
        else if (p.t === 'line') { xs.push(p.a[0], p.b[0]); ys.push(p.a[1], p.b[1]); }
        else if (p.t === 'ellipse') { xs.push(p.cx - p.rx, p.cx + p.rx); ys.push(p.cy - p.ry, p.cy + p.ry); }
        else if (p.t === 'circle') { xs.push(p.cx - p.r, p.cx + p.r); ys.push(p.cy - p.r, p.cy + p.r); }
        else if (p.t === 'arc') { xs.push(p.x1, p.x2); ys.push(p.y1 - p.ry, p.y1 + p.ry, p.y2 - p.ry, p.y2 + p.ry); }
    }
    return { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) };
};

/** Émission SVG : translation (x0, y0) + échelle s, pointillés pour le caché. */
const emettre = (parts, x0, y0, s = 1) => {
    const X = (x) => (x0 + x * s).toFixed(1);
    const Y = (y) => (y0 + y * s).toFixed(1);
    // linejoin/linecap ROUND : le miter par défaut fait dépasser une pointe
    // aux sommets aigus (apex du cône, de la pyramide...) — signalé prof
    const trait = (dash) => `stroke="${BLEU}" stroke-width="${dash ? 1.5 : 2}" stroke-linejoin="round" stroke-linecap="round"${dash ? ' stroke-dasharray="5 4"' : ''}`;
    const out = [];
    for (const p of parts) {
        if (p.t === 'poly') {
            out.push(`<polygon points="${p.pts.map(([x, y]) => `${X(x)},${Y(y)}`).join(' ')}" fill="${p.fill}" ${trait(false)}/>`);
        } else if (p.t === 'line') {
            out.push(`<line x1="${X(p.a[0])}" y1="${Y(p.a[1])}" x2="${X(p.b[0])}" y2="${Y(p.b[1])}" ${trait(p.dash)}/>`);
        } else if (p.t === 'ellipse') {
            out.push(`<ellipse cx="${X(p.cx)}" cy="${Y(p.cy)}" rx="${(p.rx * s).toFixed(1)}" ry="${(p.ry * s).toFixed(1)}" fill="${FACE2}" ${trait(false)}/>`);
        } else if (p.t === 'circle') {
            out.push(`<circle cx="${X(p.cx)}" cy="${Y(p.cy)}" r="${(p.r * s).toFixed(1)}" fill="${FACE1}" ${trait(false)}/>`);
        } else if (p.t === 'arc') {
            out.push(`<path d="M ${X(p.x1)} ${Y(p.y1)} A ${(p.rx * s).toFixed(1)} ${(p.ry * s).toFixed(1)} 0 0 ${p.sweep} ${X(p.x2)} ${Y(p.y2)}" fill="none" ${trait(p.dash)}/>`);
        }
    }
    return out;
};

/** Un solide posé dans une case : normalisé, centré, mis à l'échelle. */
const poserSolide = (type, cx, cy, maxW, maxH) => {
    const parts = SOLIDES[type]();
    const bb = bboxDe(parts);
    const w = bb.maxX - bb.minX;
    const h = bb.maxY - bb.minY;
    const s = Math.min(1, maxW / w, maxH / h);
    const x0 = cx - (w * s) / 2 - bb.minX * s;
    const y0 = cy - (h * s) / 2 - bb.minY * s;
    return emettre(parts, x0, y0, s);
};

// ─── Noms, confusions, verrous ────────────────────────────────────────────

const TYPES = ['cube', 'pave', 'pyramide', 'prisme', 'cylindre', 'cone', 'boule'];

const NOMS = {
    cube: 'un cube',
    pave: 'un pavé droit',
    pyramide: 'une pyramide',
    prisme: 'un prisme droit',
    cylindre: 'un cylindre',
    cone: 'un cône',
    boule: 'une boule',
};
const PLURIELS = {
    cube: 'des cubes',
    pave: 'des pavés droits',
    pyramide: 'des pyramides',
    prisme: 'des prismes droits',
    cylindre: 'des cylindres',
    cone: 'des cônes',
    boule: 'des boules',
};

/** Confusions voisines servies en distracteurs de F1 (« le plus précis »
 *  rend les inclusions vraies non défendables : cube EST un pavé, mais
 *  « cube » est plus précis). */
const CONFUSIONS = {
    cube: ['pave', 'prisme', 'pyramide'],
    pave: ['cube', 'prisme', 'cylindre'],
    pyramide: ['cone', 'prisme', 'cube'],
    prisme: ['pave', 'pyramide', 'cylindre'],
    cylindre: ['cone', 'prisme', 'boule'],
    cone: ['pyramide', 'cylindre', 'boule'],
    boule: ['cylindre', 'cone', 'pyramide'],
};

/** Verrous d'inclusion des planches : jamais un solide de la planche qui
 *  SOIT un exemplaire de la cible (item 9 : le cube est un pavé droit ; le
 *  cube et le pavé sont des prismes droits). */
const EXCLUS = {
    pave: ['cube'],
    prisme: ['cube', 'pave'],
};

// ─── F1 : nommer le solide ────────────────────────────────────────────────

const genNommerQcm = () => {
    const type = randomChoice(TYPES);
    const parts = SOLIDES[type]();
    const bb = bboxDe(parts);
    const W = Math.ceil(bb.maxX - bb.minX) + 28;
    const H = Math.ceil(bb.maxY - bb.minY) + 28;
    const svg = [
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${Math.min(220, W * 1.6)}" role="img"`,
        '  aria-label="Un solide dessiné en perspective, arêtes cachées en pointillés.">',
        '  <g data-fig="0">',
        '  ' + emettre(parts, 14 - bb.minX, 14 - bb.minY, 1).join('\n  '),
        '  </g>',
        '</svg>',
    ].join('\n');
    const dis = CONFUSIONS[type].map((t) => ({ valeur: NOMS[t], code: `confusion_${t}` }));
    const payload = makeQcm(
        [NOMS[type], ...dis.map((d) => d.valeur)],
        NOMS[type],
        '<p><b>Quel est le nom le plus précis de ce solide ?</b></p><p>{{element:0}}</p>',
        { ...opts(L1), elements_additionnels: elImage(dataUrl(svg)) },
        false,
    );
    payload.contenu = { ...payload.contenu, sousType: `nommer_${type}` };
    payload._meta.params = { sousType: `nommer_${type}`, distracteurs: dis };
    return payload;
};

// ─── F2 : trouver le solide sur la planche 2×2 ────────────────────────────

const CASES4 = [[0, 0], [156, 0], [0, 116], [156, 116]];

const svgPlanche = (types, cases, vbW, vbH, largeur, maxW, maxH) => {
    const p = [];
    p.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vbW} ${vbH}" width="${largeur}" role="img"`);
    p.push(`  aria-label="${types.length} solides en perspective numérotés, arêtes cachées en pointillés.">`);
    types.forEach((type, k) => {
        const [cx, cy] = cases[k];
        p.push(`  <text x="${cx + 13}" y="${cy + 19}" font-size="15" font-weight="700" fill="#111827" font-family="system-ui,sans-serif" data-num="${k}">${NUMS[k]}</text>`);
        p.push(`  <g data-fig="${k}">`);
        p.push('  ' + poserSolide(type, cx + cases.cw / 2, cy + cases.ch / 2 + 6, maxW, maxH).join('\n  '));
        p.push('  </g>');
    });
    p.push('</svg>');
    return p.join('\n');
};

const genTrouverQcm = () => {
    const cible = randomChoice(TYPES);
    const interdits = [cible, ...(EXCLUS[cible] || [])];
    const autres = randomChoiceMultiple(TYPES.filter((t) => !interdits.includes(t)), 3);
    const types = shuffle([cible, ...autres]);
    const cases = [...CASES4];
    cases.cw = 156;
    cases.ch = 116;
    const svg = svgPlanche(types, cases, 312, 232, 300, 116, 92);
    const idx = types.indexOf(cible);
    const dis = types
        .map((t, k) => ({ t, k }))
        .filter(({ k }) => k !== idx)
        .map(({ t, k }) => ({ valeur: `Figure ${NUMS[k]}`, code: `figure_${t}` }));
    const payload = makeQcm(
        [`Figure ${NUMS[idx]}`, ...dis.map((d) => d.valeur)],
        `Figure ${NUMS[idx]}`,
        `<p><b>Lequel de ces solides est ${NOMS[cible]} ?</b></p><p>{{element:0}}</p>`,
        { ...opts(L1), elements_additionnels: elImage(dataUrl(svg)) },
        false,
    );
    payload.contenu = { ...payload.contenu, sousType: `trouver_${cible}` };
    payload._meta.params = { sousType: `trouver_${cible}`, types, distracteurs: dis };
    return payload;
};

// ─── F3 : trier la planche 6 en multi-sélection ───────────────────────────

const CASES6 = [[0, 0], [140, 0], [280, 0], [0, 132], [140, 132], [280, 132]];

const genTrierMulti = () => {
    const cible = randomChoice(TYPES);
    const interdits = [cible, ...(EXCLUS[cible] || [])];
    const vivier = TYPES.filter((t) => !interdits.includes(t));
    // cible « prisme » : le vivier ne compte que 4 autres types → au moins
    // 2 exemplaires de la cible pour remplir les 6 cases (mesuré : planche
    // à 5 solides sinon, attrapée par le solveur)
    const nCible = randInt(Math.max(1, 6 - vivier.length), 3);
    const autres = randomChoiceMultiple(vivier, 6 - nCible);
    const types = shuffle([...Array(nCible).fill(cible), ...autres]);
    garde(types.length === 6, 'planche trier incomplète');
    const cases = [...CASES6];
    cases.cw = 140;
    cases.ch = 132;
    const svg = svgPlanche(types, cases, 420, 264, 340, 106, 100);
    const choix = types.map((_, k) => `Figure ${NUMS[k]}`);
    const bonnes = types.map((t, k) => (t === cible ? `Figure ${NUMS[k]}` : null)).filter(Boolean);
    garde(bonnes.length === nCible, 'planche trier incohérente');
    const payload = makeQcm(
        choix,
        bonnes,
        `<p><b>Lesquels de ces solides sont ${PLURIELS[cible]} ?</b></p><p>{{element:0}}</p>`,
        { ...opts(L1), elements_additionnels: elImage(dataUrl(svg)) },
        true,
    );
    payload.contenu = { ...payload.contenu, sousType: `trier_${cible}` };
    payload._meta.params = { sousType: `trier_${cible}`, types, nCible };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'IDENTIFIER seulement : pas de comptage de faces/arêtes/sommets, pas d\'inclusion questionnée, pas d\'objets du quotidien (arbitrages prof 2026-08-22)',
        'perspective cavalière, ARÊTES CACHÉES EN POINTILLÉS (au programme — corrige la fiche source) ; variantes par proportions et sens de fuyante, vues canoniques',
        'verrous d\'inclusion : cible pavé → jamais de cube dans la planche ; cible prisme → jamais de cube ni de pavé (prismes droits particuliers) ; « le plus précis » en F1',
        'le pavé a toujours une face avant NON carrée (ratio ≥ 1,45) : cube/pavé discernables sur le dessin',
    ],
    nommer_qcm: {
        fixes: { confusions: 'les voisins de chaque solide (cube↔pavé↔prisme, cône↔pyramide↔cylindre, boule↔ronds) — cf. CONFUSIONS' },
    },
    trouver_qcm: {
        fixes: { figures: 'les trois autres solides de la planche, tous de classes différentes de la cible (verrous d\'inclusion compris)' },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    nommer_qcm: genNommerQcm,
    trouver_qcm: genTrouverQcm,
    trier_multi: genTrierMulti,
};

const FORMAT_LABELS = {
    nommer_qcm: 'Nommer le solide',
    trouver_qcm: 'Trouver le solide',
    trier_multi: 'Trier les solides',
};

export const CATEGORIES = [
    {
        id: 'solides',
        label: L1,
        formatsList: Object.keys(GENERATORS).map((k) => ({ key: k, label: FORMAT_LABELS[k] || k })),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

// Réutilisés par gen_1238 (3e, reconnaître les solides : extension aux six
// solides du programme de 3e) : primitives, planches, noms et verrous.
export { SOLIDES, bboxDe, emettre, poserSolide, svgPlanche, TYPES, NOMS, PLURIELS, CONFUSIONS, EXCLUS, NUMS, CASES4, dataUrl, elImage };
